import React from 'react';
import { useLanguage } from '../../../i18n';

const BarSegment = ({ value, scaleMax, bgColor, segmentIndex, totalSegments }) => {
  const pct = scaleMax > 0 ? (value / scaleMax) * 100 : 0;
  const borderRadius = segmentIndex === 0
    ? '4px 0 0 4px'
    : segmentIndex === totalSegments - 1
      ? '0 4px 4px 0'
      : '0';
  if (value <= 0) return null;
  return (
    <div style={{
      width: `${pct}%`,
      flexShrink: 0,
      height: '36px',
      background: bgColor,
      borderRadius,
    }} />
  );
};

const BarLabels = ({ segments, scaleMax, formatValue }) => {
  let cumPct = 0;
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
      {segments.map((seg, i) => {
        const pct = scaleMax > 0 ? (seg.value / scaleMax) * 100 : 0;
        const centerPct = cumPct + pct / 2;
        cumPct += pct;
        if (seg.value <= 0) return null;
        return (
          <span key={i} style={{
            position: 'absolute',
            left: `${centerPct}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            fontSize: '0.68rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          }}>
            {formatValue(seg.value)}
          </span>
        );
      })}
    </div>
  );
};

const ResultsBarChart = ({ calculationResult, convertToDisplayCurrency }) => {
  const { t } = useLanguage();
  const [showInvestTooltip, setShowInvestTooltip] = React.useState(false);

  const investBeginSeparatus = convertToDisplayCurrency(calculationResult.invest_beginn_separatus || 0);
  const investBeginKonv = convertToDisplayCurrency(calculationResult.invest_beginn_konventionelle || 0);
  const investBeginLuft = convertToDisplayCurrency(calculationResult.invest_beginn_luftwaerme || 0);

  const aliquotSeparatus = convertToDisplayCurrency(calculationResult.erneuerungskosten_separatus_aliquot || 0);
  const aliquotKonv = convertToDisplayCurrency(calculationResult.erneuerungskosten_konventionelle_aliquot || 0);
  const aliquotLuft = convertToDisplayCurrency(calculationResult.erneuerungskosten_luftwaerme_aliquot || 0);

  const opCostSeparatus = convertToDisplayCurrency(calculationResult.betrieb25j_separatus || 0);
  const opCostKonv = convertToDisplayCurrency(calculationResult.betrieb25j_konventionelle || 0);
  const opCostLuft = convertToDisplayCurrency(calculationResult.betrieb25j_luftwaerme || 0);

  const totalSeparatus = convertToDisplayCurrency(calculationResult.vollkosten_separatus || 0);
  const totalKonv = convertToDisplayCurrency(calculationResult.vollkosten_konventionelle || 0);
  const totalLuft = convertToDisplayCurrency(calculationResult.vollkosten_luftwaerme || 0);

  const maxTotal = Math.max(totalSeparatus, totalKonv, totalLuft);
  const scaleMax = Math.ceil(maxTotal / 1000) * 1000;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const targetDivisions = isMobile ? 3 : 5;

  const roughStep = scaleMax / targetDivisions;
  const niceStep = Math.ceil(roughStep / 5000) * 5000;

  const scaleLabels = [];
  for (let v = 0; v <= scaleMax; v += niceStep) {
    scaleLabels.push(v);
  }
  if (scaleLabels[scaleLabels.length - 1] < scaleMax) {
    scaleLabels.push(scaleMax);
  }
  if (scaleLabels.length >= 3) {
    const secondToLast = scaleLabels[scaleLabels.length - 2];
    if ((scaleMax - secondToLast) / scaleMax < 0.15) {
      scaleLabels.splice(scaleLabels.length - 2, 1);
    }
  }
  if (isMobile && scaleLabels.length > 3) {
    const midValue = Math.round(scaleMax / 2 / 5000) * 5000;
    scaleLabels.length = 0;
    scaleLabels.push(0, midValue, scaleMax);
  }

  const formatValue = (val) => val.toLocaleString(calculationResult.display_locale || 'de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const currency = calculationResult.currency || 'CHF';

  const systems = [
    {
      label: t('systemSeparatus'),
      invest: investBeginSeparatus, aliquot: aliquotSeparatus, opCost: opCostSeparatus,
      investColor: '#2E7D32', aliquotColor: '#A5D6A7', opColor: '#81C784',
    },
    {
      label: t('systemKonv'),
      invest: investBeginKonv, aliquot: aliquotKonv, opCost: opCostKonv,
      investColor: '#E65100', aliquotColor: '#FFCC80', opColor: '#FFB74D',
    },
    {
      label: t('systemLuft'),
      invest: investBeginLuft, aliquot: aliquotLuft, opCost: opCostLuft,
      investColor: '#0D47A1', aliquotColor: '#90CAF9', opColor: '#64B5F6',
    },
  ];

  return (
    <div style={{ marginBottom: '2rem', background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#333' }}>{t('fullCostComparison')}</h3>
        <div 
          style={{ 
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '16px', height: '16px', borderRadius: '50%', background: '#e0e0e0',
            color: '#666', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer', position: 'relative'
          }}
          onMouseEnter={() => setShowInvestTooltip(true)}
          onMouseLeave={() => setShowInvestTooltip(false)}
          onClick={() => setShowInvestTooltip(!showInvestTooltip)}
          data-testid="invest-info-icon"
        >
          i
          {showInvestTooltip && (
            <div style={{
              position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
              marginBottom: '8px', padding: '10px 14px', background: '#333', color: 'white',
              borderRadius: '8px', fontSize: '0.8rem', fontWeight: 'normal', whiteSpace: 'normal',
              zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', width: '280px', textAlign: 'left', lineHeight: '1.4'
            }}>
              {t('fullCostTooltip')}
              <div style={{ position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #333' }}></div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '1rem', fontSize: '0.8rem', lineHeight: '2' }}>
        <span style={{ borderLeft: '10px solid #2E7D32', paddingLeft: '5px', marginRight: '18px' }}>{t('legendInvest')}</span>
        <span style={{ borderLeft: '10px solid #A5D6A7', paddingLeft: '5px', marginRight: '18px' }}>{t('legendRenewal')}</span>
        <span style={{ borderLeft: '10px solid #81C784', paddingLeft: '5px' }}>{t('legendOperation')}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {systems.map((sys, idx) => {
          const segs = [
            { value: sys.invest, color: sys.investColor },
            { value: sys.aliquot, color: sys.aliquotColor },
            { value: sys.opCost, color: sys.opColor },
          ];
          return (
            <div key={idx}>
              <div style={{ fontSize: '0.8rem', marginBottom: '0.25rem', color: '#333' }}>{sys.label}</div>
              <div style={{ position: 'relative', height: '36px' }}>
                <div style={{ display: 'flex', height: '36px', borderRadius: '4px', overflow: 'hidden' }}>
                  <BarSegment value={sys.invest} scaleMax={scaleMax} bgColor={sys.investColor} segmentIndex={0} totalSegments={3} />
                  <BarSegment value={sys.aliquot} scaleMax={scaleMax} bgColor={sys.aliquotColor} segmentIndex={1} totalSegments={3} />
                  <BarSegment value={sys.opCost} scaleMax={scaleMax} bgColor={sys.opColor} segmentIndex={2} totalSegments={3} />
                </div>
                <BarLabels segments={segs} scaleMax={scaleMax} formatValue={formatValue} />
              </div>
            </div>
          );
        })}

        <div style={{ position: 'relative', height: '28px', borderTop: '1px solid #ccc', marginTop: '0.5rem' }}>
          {scaleLabels.map((value, i) => {
            const leftPct = scaleMax > 0 ? (value / scaleMax) * 100 : 0;
            const isFirst = i === 0;
            const isLast = i === scaleLabels.length - 1;
            const xShift = isFirst ? '0%' : isLast ? '-100%' : '-50%';
            return (
              <div key={i} style={{
                position: 'absolute',
                left: `${leftPct}%`,
                transform: `translateX(${xShift})`,
                fontSize: '0.7rem',
                color: '#666',
                textAlign: 'center',
                paddingTop: '8px',
                whiteSpace: 'nowrap',
              }}>
                <div style={{ position: 'absolute', top: '-6px', left: isFirst ? '0' : isLast ? '100%' : '50%', transform: isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)', width: '1px', height: '6px', background: '#ccc' }}></div>
                {formatValue(value)}
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#666', marginTop: '-0.25rem' }}>
          {t('fullCostIn')} {currency}
        </div>
      </div>
    </div>
  );
};

export default ResultsBarChart;
